import type { Component } from 'solid-js'
import { A, useLocation, useNavigate } from '@solidjs/router'
import { Logger } from 'besonders-logger'
import { createMemo, createResource, createSignal, Show } from 'solid-js'
import { initialized } from '../../appInit'
import { devMode, setDevMode, useZenMode } from '../../ui/utils-ui'
import { Iconify } from '../mini-components'
import { SyncButton } from '../SyncControls'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

export const TopBar: Component<{ searchOpen; setSearchOpen }> = (props) => {
	const location = useLocation()
	const navigate = useNavigate()
	const isSearchPossible = createMemo(() => !(['settings', 'help'].includes(location.pathname.split('/')[1])))
	// Same URL-backed state the desktop keybinding (Ctrl+Shift+Z, see data/keybindings.ts)
	// toggles — the dropdown below is the mobile-friendly (tap, not keybinding) entry point.
	const [zenMode, setZenMode] = useZenMode()
	const onOverflowSelect = (evt: any) => {
		const action = evt.detail.item.value
		if (action === 'zen') setZenMode(!zenMode())
		else if (action === 'settings') navigate('/settings')
	}

	const [versionStrStatic] = createResource(async () => {
		if (import.meta.env.DEV && import.meta.env.PREV_COMMIT) {
			DEBUG({ metaEnv: import.meta.env })
			return `${import.meta.env.PREV_COMMIT}_${import.meta.env.GIT_STATUS_INFO}`
		} else {
			DEBUG('!DEV', import.meta.env.DEV)
		}
		const response = await fetch('commit.txt') // (i) using relative path, bc. we might be on e.g. /ipns/note3.fission.app/#/
		if (!response.ok) throw ERROR(`Response ${response.statusText} for /commit.txt`)
		const text = await response.text()
		return text // .substring(7)
	}, { initialValue: import.meta.env.PREV_COMMIT ? `${import.meta.env.PREV_COMMIT}_${import.meta.env.GIT_STATUS_INFO}` : '' })
	const [versionStrHMR, setVersionStrHMR] = createSignal(null)
	if (import.meta.hot) {
		import.meta.hot.on('git:hmr', (data) => {
			DEBUG('git:hmr', { data })
			if (data.PREV_COMMIT) {
				setVersionStrHMR(`${data.PREV_COMMIT}_${data.GIT_STATUS_INFO}`)
			}
		})
	}
	const versionStr = createMemo(() => (versionStrHMR() || versionStrStatic())?.substring(0, 7))
	const gitlabURL = createMemo(() =>
		`https://gitlab.com/wovin/wovin/-/${
			versionStr()?.slice(-5).includes('A')
				? 'commits/main' // send to main commits page if our hash is ahead
				: `commit/${versionStrStatic()?.substring(0, 7)}` // otherwise try the sha (hack: might 404 on D or B)
		}`
	)
	DEBUG({ versionStr: versionStrStatic(), sig: versionStrHMR(), gitlabURL: gitlabURL() })

	return (
		<header
			class={!props.searchOpen() ? 'flex' : 'hidden'}
			data-route={location.pathname}
			w-full
			flex='items-center justify-between'
			relative
		>
			{/* <Show when={!searchOpen()} fallback={<OverlaySearchBar open={searchOpen} setOpen={setSearchOpen} />}> */}
			<div flex='~ items-center' h-10 font-900 pr-2 gap-2>
				<A class='cursor-pointer gap-2 a-unstyled' flex='~ items-center' href='/'>
					<Iconify size={7} name='i-custom:ipld' />
					<div flex='~ items-end' gap-2>
						Note3
						<Show when={versionStr()}>
							<a
								href={gitlabURL()}
								class='color-black'
								text-xs
								opacity-50
								no-underline
								target='_blank'
								font-mono
							>
								{versionStr()}
							</a>
							<Show when={devMode()}>
								<em text-xs opacity-40 ml-4>useRoots (debug)</em>
							</Show>
						</Show>
					</div>
				</A>
			</div>
			<div flex='~ items-center justify-end' gap-4>
				<Show when={isSearchPossible()}>
					{
						/* <wa-button
						// variant='text'
						onclick={() => props.setSearchOpen(true)}
						// size='s'
						mr--2
					> */
					}
					<Iconify onclick={() => props.setSearchOpen(true)} size={6} name='magnifying-glass-bold' />
					{/* </wa-button> */}
				</Show>
				{
					/*
					Mobile-friendly overflow menu: Zen mode has no on-screen affordance otherwise
					(desktop-only Ctrl+Shift+Z keybinding, see useGlobalInputHandlers in
					data/keybindings.ts) — a phone with no keyboard couldn't toggle it. Settings
					lives here now too — the standalone gear button below is hidden so there's a
					single, unambiguous way to reach Settings from the top bar.
				*/
				}
				<wa-dropdown placement='bottom-end' class='bullet-menu menu-tight' onwa-select={onOverflowSelect}>
					<Iconify slot='trigger' size={6} name='dots-three-circle' class='cursor-pointer' />
					<wa-dropdown-item class='-tight' value='zen'>
						<Iconify name={zenMode() ? 'eye-slash' : 'eye'} slot='icon' />
						{zenMode() ? 'Exit Zen mode' : 'Zen mode'}
					</wa-dropdown-item>
					<wa-dropdown-item class='-tight' value='settings'>
						<Iconify name='gear-thin' slot='icon' />
						Settings
					</wa-dropdown-item>
				</wa-dropdown>
				<Show when={location.pathname.startsWith('/settings')}>
					<A
						text='text'
						// href='/help'
						href='/focus/4b9f705?share=k51qzi5uqu5dhqzv78qeivgxoytegb5qe8c3lbhrr5z0yfuxw5q8ubdbbqt6j5' // HACK: not offline available (yet - import car file from assets?)
					>
						<Iconify size={6} name='seal-question-light' />
					</A>
				</Show>
				<Show when={initialized() /* //TODO only show button if storage is setup (or disable / > hint) */}>
					<SyncButton />
				</Show>

				{
					/*
					Standalone gear hidden — Settings now lives in the 3-dot overflow dropdown
					above. Kept in the DOM (display:none, not removed) so the middle-click
					devMode toggle keeps a home; revisit if that toggle gets its own affordance.
				*/
				}
				<A
					href='/settings'
					title={location.pathname.startsWith('/settings') ? 'already here' : 'Preferences and Settings'}
					style={{ display: 'none' }}
				>
					<wa-button
						circle
						variant={location.pathname.startsWith('/settings') ? 'primary' : 'default'}
						disabled={!!location.pathname.startsWith('/settings')}
						onAuxClick={(e: MouseEvent) => {
							if (e.button === 1) {
								e.preventDefault()
								setDevMode(!devMode())
							}
						}}
					>
						<Iconify size={7} name='gear-thin' style={{ 'margin-top': '-2px' }} />
					</wa-button>
				</A>
			</div>
			{/* </Show> */}
		</header>
	)
}
